import type { JSX } from 'react';
import { Divider, Link, Text } from '@ksyos/design-system';
import { useNavigate, useSearchParams } from '@ksyos/router';
import { useCallback, useEffect, useState } from 'react';
import type {
    DefinedCustomControlData,
    IProcessAction,
    IProcessActivity,
    IServiceStartingPoint,
} from '@common/interfaces';
import type { CreateProcessRequestBody, PerformProcessActionRequestBody } from '@common/interfaces/api/patientsApi';
import type { PerformProcessActionResult } from '@common/interfaces/performProcessAction';
import type { ProcessActivityCancelValidated } from '@common/validation/processActivityCancel';
import { EpisodeCategory } from '@common/enums';
import { Loader } from '@frontend/components/elements/common';
import { Process } from '@frontend/components/pages/episodeCategory/carePath/process/Process';
import { ProcessStart } from '@frontend/components/pages/episodeCategory/carePath/process/ProcessStart';
import { getServiceStartingPoints } from '@frontend/services/api/episodeCategoriesApiService';
import { createProcess, deleteProcessAction, performProcessAction } from '@frontend/services/api/patientsApiService';
import { cancelProcessActivity, updateCustomControlData } from '@frontend/services/api/processActivitiesApiService';
import { getRequestProcesses } from '@frontend/services/api/requestProcessesApiService';
import { scrollToContainerItemOrTop } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { startView } from '@frontend/utils/hooks/useDatadogView';
import { generateUrlForRoute } from '@frontend/utils/hooks/useNavigateTo';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import { useSplitViewContext } from '@frontend/utils/hooks/useSplitViewContext';
import { isSplitViewEnabledForCarePath } from '@frontend/utils/isSplitViewEnabledForCarePath';
import { PatientEpisodeCategoryCarePathContextProvider } from './PatientEpisodeCategoryCarePathContextProvider';
import { ProcessStartWrapper } from './ProcessStartWrapper';

export function PatientEpisodeCategoryCarePath(): JSX.Element {
    const abortSignal = useAbortSignal();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { episodeCategory, patient, processes, reload, setSaveStatus } = usePatientEpisodeCategoryContext();
    const [activeModifyActivity, setActiveModifyActivity] = useState<IProcessActivity>();
    const [activeProcessAction, setActiveProcessAction] = useState<IProcessAction>();
    const [activeStartingPoint, setActiveStartingPoint] = useState<IServiceStartingPoint>();
    const [isSavingAndReloading, setIsSavingAndReloading] = useState(false);
    const [hasNonFlexProcessesForCarePath, setHasNonFlexProcessesForCarePath] = useState(false);
    const { isSplitViewOpen, setIsSplitViewAvailable, splitViewContent } = useSplitViewContext();
    const startingPointId = searchParams.get('startingPointId');

    useEffect(() => {
        if (startingPointId && !activeStartingPoint) {
            loadStartingPointFromUrl(episodeCategory, patient.id);
        }
    }, [activeStartingPoint, episodeCategory, patient.id, startingPointId]);

    useEffect(() => {
        getNonFlexProcessesForCarePath(episodeCategory, patient.id);
    }, [episodeCategory, patient.id]);

    useEffect(() => {
        if (!isSplitViewEnabledForCarePath(episodeCategory)) {
            return;
        }
        // The third condition prevents an already open splitview from closing shortly in case of isSavingAndReloading
        // (for example, after deleting a draft).
        if (
            activeProcessAction ||
            activeModifyActivity ||
            (isSavingAndReloading && !isSplitViewOpen) ||
            !splitViewContent
        ) {
            setIsSplitViewAvailable(false);
        } else if (splitViewContent) {
            setIsSplitViewAvailable(true);
        }
    }, [
        activeProcessAction,
        activeModifyActivity,
        episodeCategory,
        splitViewContent,
        isSavingAndReloading,
        isSplitViewOpen,
    ]);

    useEffect(() => {
        setActivityViewName();
    }, [activeModifyActivity, activeProcessAction, activeStartingPoint, episodeCategory, patient.id]);

    return (
        <PatientEpisodeCategoryCarePathContextProvider
            activeModifyActivity={activeModifyActivity}
            activeProcessAction={activeProcessAction}
            activeStartingPoint={activeStartingPoint}
            cancelProcessActivityAndReload={useCallback(cancelProcessActivityAndReload, [patient.id])}
            deleteProcessActionDraft={useCallback(deleteProcessActionDraft, [patient.id])}
            isSavingAndReloading={isSavingAndReloading}
            saveModifiedCustomControlData={useCallback(saveModifiedCustomControlData, [patient.id])}
            saveProcess={useCallback(saveProcess, [patient.id])}
            saveProcessAction={useCallback(saveProcessAction, [patient.id])}
            setActiveModifyActivity={useCallback(setActiveModifyActivity, [])}
            setActiveProcessAction={useCallback(setActiveProcessAction, [])}
            setActiveStartingPoint={useCallback(setActiveStartingPoint, [])}
        >
            {isSavingAndReloading && (
                <Loader
                    centered={true}
                    isBlocking={true}
                    large={true}
                />
            )}

            {!activeStartingPoint && !activeProcessAction && !isSavingAndReloading && (
                <ProcessStartWrapper
                    episodeCategory={episodeCategory}
                    isEmptyEpisode={processes.length === 0}
                    onStartingPointSelected={setActiveStartingPoint}
                    patientId={patient.id}
                />
            )}

            {/* New process that has not been saved yet */}
            {activeStartingPoint && <ProcessStart />}

            {/* Already existing processes */}
            {processes.map((process) => (
                <Process
                    key={process.id}
                    process={process}
                />
            ))}

            {hasNonFlexProcessesForCarePath && (
                <>
                    <Divider spacing="small" />
                    <Text>
                        <Link
                            to={`/episode?patientId=${patient.id}&episodeCategory=${episodeCategory}`}
                            variant="link"
                        >
                            Ga naar voorgaande zorgpaden
                        </Link>
                    </Text>
                </>
            )}
        </PatientEpisodeCategoryCarePathContextProvider>
    );

    async function loadStartingPointFromUrl(episodeCategoryName: EpisodeCategory, patientId: string): Promise<void> {
        const resultStartingPoints = await getServiceStartingPoints(episodeCategoryName, patientId, abortSignal);
        const nextStartingPoint = resultStartingPoints.find((startingPoint) => startingPoint.id === startingPointId);
        if (nextStartingPoint) {
            setActiveStartingPoint(nextStartingPoint);
            removeStartingPointFromUrl();
        }
    }

    async function getNonFlexProcessesForCarePath(
        episodeCategoryName: EpisodeCategory,
        patientId: string
    ): Promise<void> {
        if (episodeCategoryName === EpisodeCategory.somnology) {
            const requests = await getRequestProcesses({
                episodeCategory: episodeCategoryName,
                patientId,
                signal: abortSignal,
            });
            setHasNonFlexProcessesForCarePath(!!requests.length);
        }
    }

    function removeStartingPointFromUrl() {
        const url = new URL(window.location.href);
        url.searchParams.delete('startingPointId');
        // Replace ensures correct behavior between different browsers when navigating backwards
        navigate(url.href.substring(url.origin.length), { replace: true });
    }

    async function onSaveComplete(): Promise<void> {
        setActiveModifyActivity(undefined);
        setActiveProcessAction(undefined);
        setActiveStartingPoint(undefined);
        await reload();
        scrollToContainerItemOrTop();
        setIsSavingAndReloading(false);
    }

    async function deleteProcessActionDraft(processAction: IProcessAction): Promise<void> {
        if (!processAction.processWorkflowStepId) {
            throw new Error('Expected process action to contain processWorkflowStepId');
        }

        setIsSavingAndReloading(true);
        await deleteProcessAction(
            patient.id,
            processAction.processId,
            processAction.id,
            {
                processWorkflowStepId: processAction.processWorkflowStepId,
            },
            abortSignal
        );
        await onSaveComplete();
    }

    async function saveProcess(process: CreateProcessRequestBody): Promise<void> {
        setIsSavingAndReloading(true);
        await createProcess(patient.id, process, abortSignal);
        await onSaveComplete();
    }

    async function saveProcessAction(
        processId: string,
        processAction: PerformProcessActionRequestBody
    ): Promise<PerformProcessActionResult> {
        let performProcessActionResult: PerformProcessActionResult;
        if ('isAutoSave' in processAction && processAction.isAutoSave) {
            setSaveStatus('saving');
            performProcessActionResult = await performProcessAction(patient.id, processId, processAction, abortSignal);
            setSaveStatus('saved');
        } else {
            setIsSavingAndReloading(true);
            performProcessActionResult = await performProcessAction(patient.id, processId, processAction, abortSignal);
            await onSaveComplete();
        }

        return performProcessActionResult;
    }

    async function saveModifiedCustomControlData(
        processActivityId: string,
        customControlData: DefinedCustomControlData
    ): Promise<void> {
        setIsSavingAndReloading(true);
        await updateCustomControlData(processActivityId, customControlData, abortSignal);
        await onSaveComplete();
    }

    async function cancelProcessActivityAndReload(
        processActivityId: string,
        reason: ProcessActivityCancelValidated
    ): Promise<void> {
        setIsSavingAndReloading(true);
        await cancelProcessActivity(processActivityId, reason, abortSignal);
        await onSaveComplete();
    }

    function setActivityViewName() {
        const baseViewName = generateUrlForRoute('patientCarePath', {
            activeTab: 'carePath',
            episodeCategory,
            patientId: patient.id,
        });
        if (activeModifyActivity) {
            startView(`${baseViewName}/${activeModifyActivity.serviceActivity.customControlCode}/modify`);
        } else if (activeProcessAction) {
            startView(`${baseViewName}/${activeProcessAction.activities?.[0].customControlCode}`);
        } else if (activeStartingPoint) {
            startView(`${baseViewName}/${activeStartingPoint.activities?.[0].customControlCode}`);
        } else {
            startView(baseViewName);
        }
    }
}
